import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, message, Typography, Steps, Image, Alert } from 'antd';
import { UserOutlined, SafetyOutlined, LockOutlined, KeyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Setup: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [needsSetup, setNeedsSetup] = useState(true);
  const [qrcodeUrl, setQrcodeUrl] = useState<string>('');
  const [totpSecret, setTotpSecret] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/setup/check`);
      const { needsSetup } = response.data;

      if (!needsSetup) {
        // 已初始化，跳转到登录页
        navigate('/login');
      }
      setNeedsSetup(needsSetup);
    } catch (error) {
      console.error('检查初始化状态失败:', error);
    }
  };

  const onFinish = async (values: {
    telegramId?: string;
    username: string;
    loginUsername: string;
    password: string;
    confirmPassword: string;
  }) => {
    // 验证密码一致性
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/setup/init`, {
        telegramId: values.telegramId ? parseInt(values.telegramId) : undefined,
        username: values.username,
        loginUsername: values.loginUsername,
        password: values.password,
      });

      // 保存 2FA 信息
      setQrcodeUrl(response.data.qrcodeUrl);
      setTotpSecret(response.data.totpSecret);

      message.success('初始化成功！请扫描二维码设置 2FA');
      setCurrentStep(1); // 跳转到二维码页面
    } catch (error: any) {
      message.error(error.response?.data?.error || '初始化失败');
    } finally {
      setLoading(false);
    }
  };

  if (!needsSetup) {
    return null; // 正在跳转
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
      }}
    >
      <Card style={{ maxWidth: 600, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <SafetyOutlined style={{ fontSize: 48, color: '#667eea', marginBottom: 16 }} />
          <Title level={2} style={{ marginBottom: 8 }}>
            初始化管理系统
          </Title>
          <Text type="secondary">欢迎！系统检测到这是首次使用</Text>
        </div>

        <Steps current={currentStep} style={{ marginBottom: 32 }}>
          <Step title="创建账号" description="设置用户名和密码" />
          <Step title="配置 2FA" description="扫描二维码" />
          <Step title="完成" description="开始使用系统" />
        </Steps>

        {currentStep === 0 && (
          <Form
            name="setup"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Alert
              message="创建管理员账号"
              description="设置登录用户名和密码。系统将自动生成 Google 2FA 密钥保护你的账号。"
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <Form.Item
              label="显示名称"
              name="username"
              rules={[
                { required: true, message: '请输入显示名称' },
                { min: 2, message: '至少 2 个字符' },
              ]}
              extra="用于系统内显示，可以是昵称"
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="例如：张三"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              label="登录用户名"
              name="loginUsername"
              rules={[
                { required: true, message: '请输入登录用户名' },
                { pattern: /^[a-zA-Z0-9_]{3,20}$/, message: '3-20 位字母、数字或下划线' },
              ]}
              extra="用于登录，只能包含字母、数字和下划线"
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="例如：admin"
              />
            </Form.Item>

            <Form.Item
              label="密码"
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 8, message: '至少 8 位字符' },
              ]}
              extra="建议使用字母、数字和符号组合"
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="至少 8 位"
              />
            </Form.Item>

            <Form.Item
              label="确认密码"
              name="confirmPassword"
              rules={[
                { required: true, message: '请再次输入密码' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="再次输入密码"
              />
            </Form.Item>

            <Form.Item
              label="Telegram ID（可选）"
              name="telegramId"
              rules={[
                { pattern: /^\d+$/, message: '请输入有效的数字 ID' },
              ]}
              extra="如果需要使用 Telegram 机器人功能，请填写"
            >
              <Input
                prefix={<KeyOutlined />}
                placeholder="留空或输入数字 ID"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block>
                创建账号并生成 2FA
              </Button>
            </Form.Item>
          </Form>
        )}

        {currentStep === 1 && qrcodeUrl && (
          <div>
            <Alert
              message="配置 Google 2FA"
              description="使用 Google Authenticator 或其他 TOTP 应用扫描下方二维码。"
              type="success"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <Image
                src={qrcodeUrl}
                alt="2FA QR Code"
                style={{ maxWidth: 250 }}
                preview={false}
              />
            </div>

            <Alert
              message="⚠️ 重要提示"
              description={
                <div>
                  <Paragraph>
                    • 请务必使用 <Text strong>Google Authenticator</Text> 或其他 TOTP 应用扫描上方二维码
                  </Paragraph>
                  <Paragraph>
                    • 手动输入密钥：<Text code copyable>{totpSecret}</Text>
                  </Paragraph>
                  <Paragraph>
                    • <Text type="danger">此密钥仅显示一次，请妥善保存！</Text>
                  </Paragraph>
                </div>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />

            <div style={{ textAlign: 'center' }}>
              <Button type="primary" size="large" onClick={() => {
                message.success('初始化完成！正在跳转到登录页面...');
                setTimeout(() => navigate('/login'), 2000);
              }}>
                我已保存，前往登录
              </Button>
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: 32,
            padding: 16,
            background: '#f0f2f5',
            borderRadius: 4,
          }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            💡 提示：创建的账号将拥有超级管理员权限，可以管理所有机器人和其他管理员
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Setup;
